/* eslint-disable import/no-cycle */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  ViewEncapsulation,
  afterNextRender,
  computed,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { filter, map, take } from 'rxjs'

import {
  ProcessActionTriggerEvent,
  ProcessRendererTemplateConfig,
  ResolvedAction,
  addViewProcessInstance,
  expressionHasDynamicVariables,
  getActionsByTemplateConfigAndTrigger$,
  getProcessInstanceUuidFromUrl,
  getUrlQueryParamValue,
  getViewProcessInstance,
  parseJson,
  resolveTransportJson,
  setProcessInstanceUuidInUrl,
  urlUpdate$,
} from '@flowx/core-sdk'

import { FlxProcessRendererComponent } from '../../process-renderer/process-renderer.component'
import { FlxActionService } from '../../services/flx-action.service'
import { FlxUiFlowRendererComponent } from '../../ui-flow-renderer/ui-flow-renderer.component'

@Component({
  selector: 'flx-process-renderer-wrapper',
  imports: [FlxProcessRendererComponent],
  template: `@if (config && showProcess()) {
    <flx-process-renderer
      [workspaceId]="config.workspaceId()"
      [organizationId]="config.organizationId()"
      [baseUrl]="config.baseUrl()"
      [apiUrl]="config.apiUrl()"
      [accessToken]="config.resolvedAccessToken()"
      [resourceId]="resDefId()"
      [assetsBaseUrl]="config.assetsBaseUrl()"
      [staticAssetsPath]="config.staticAssetsPath()"
      [themeId]="config.themeId()"
      [language]="config.language()"
      [locale]="config.locale()"
      [cache]="config.cache()"
      [enginePath]="config.enginePath()"
      [processApiPath]="config.processApiPath()"
      [processStartData]="processStartData()"
      [isDraft]="false"
      [projectInfo]="{ projectId: appId() }"
      [trigger]="'template'"
      (processEnd)="onProcessEnd()"
    />
  }`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FlxProcessRendererWrapperComponent implements OnInit {
  #destroyRef = inject(DestroyRef)
  #elementRef = inject(ElementRef)
  #injector = inject(Injector)
  #actionService = inject(FlxActionService)
  #processEndActions = signal<ResolvedAction[]>([])
  config = inject(FlxUiFlowRendererComponent)
  templateConfig = input.required<ProcessRendererTemplateConfig>()
  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  resDefId = computed(() => this.flowxProps()?.process?.resDefId)
  appId = computed(() => this.flowxProps()?.process?.appId)
  processStartData = computed(() => {
    const rawParams = this.flowxProps()?.process?.startParams

    if (!rawParams) {
      return {}
    }

    let processedString = rawParams
    if (expressionHasDynamicVariables(rawParams)) {
      processedString = resolveTransportJson(rawParams, this.templateConfig())
    }

    const parsed = parseJson(processedString)
    return parsed || {}
  })
  // Note: startMode is intentionally not passed to FlxProcessRendererComponent.
  // Embedded processes started from a UI Flow always use the default StartMode.MANUAL,
  // since the UI Flow orchestrates process lifecycle (start, navigation, cleanup).
  showProcess = signal<boolean>(false)

  // The view navigation (navigateToTemplateConfig) already pushed a history entry
  // for this view, so the wrapper must NOT push another one. It only needs to:
  // 1. Restore processUuid from the viewId->processUuid mapping if revisiting
  // 2. Track the mapping for new processes via the urlUpdate$ subscription
  // 3. Show the process renderer
  ngOnInit(): void {
    getActionsByTemplateConfigAndTrigger$(
      this.templateConfig(),
      ProcessActionTriggerEvent.PROCESS_END
    )
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((actions) => this.#processEndActions.set(actions))

    // Use afterNextRender to ensure the DOM is available for the closest('.flx-modal') check,
    // replacing the previous setTimeout(0) which deferred to a macrotask unnecessarily.
    afterNextRender(
      () => {
        const isInModal = this.#elementRef.nativeElement.closest('.flx-modal') !== null
        const viewId = getUrlQueryParamValue('viewId')
        const existingProcessUuid = getProcessInstanceUuidFromUrl()
        if (viewId && !existingProcessUuid && !isInModal) {
          const currentProcessUuid = getViewProcessInstance(viewId)
          if (currentProcessUuid) {
            setProcessInstanceUuidInUrl(currentProcessUuid)
            this.showProcess.set(true)
          } else {
            this.showProcess.set(true)
            urlUpdate$
              .pipe(
                map((url) => getProcessInstanceUuidFromUrl(url)),
                filter((processUuid) => !!processUuid),
                take(1),
                takeUntilDestroyed(this.#destroyRef)
              )
              .subscribe((processUuid) => {
                addViewProcessInstance(viewId, processUuid)
              })
          }
        } else {
          this.showProcess.set(true)
        }
      },
      { injector: this.#injector }
    )
  }

  async onProcessEnd(): Promise<void> {
    await this.#actionService.runActions(this.#processEndActions(), this.templateConfig())
  }
}
